import FileAttachment from "./shared/FileAttachment";
import { PRIORITY_OPTIONS, IMPACTED_MODULES } from "../../data/formOptions";
import styles from "../../styles/forms/SharedForm.module.css";
import SplitFormLayout from "../shared/SplitFormLayout";
import { safeLocalStorageGet } from "../../utils/safeJsonParse";
import { getSubmitterInfo, getDefaultCountry } from "../../utils/formHelpers";

import { useFormState } from "../../hooks/useFormState";
import { useFormSubmit } from "../../hooks/useFormSubmit";
import {
  FormSection,
  FormField,
  PinnedSubmit,
  SuccessCard,
} from "./shared/FormComponents";
import { FormProvider } from "./shared/FormContext";
import T, { useTranslate } from "../T";

const STEPS = [
  { id: "basic", label: "Basic info", desc: "Summary and description" },
  { id: "details", label: "Request details", desc: "Environment and category" },
  { id: "attachments", label: "Attachments", desc: "Relevant files" },
];

const INITIAL = {
  summary: "",
  description: "",
  environment: "Production",
  category: "New installation",
  priority: "Medium",
  platforms: "",
  attachments: [],
};

export default function PlatformRequestForm() {
  const { tText } = useTranslate();
  const {
    formData,
    handleChange,
    handleAddFiles,
    handleRemoveFile,
    setAllTouched,
    contextValue,
  } = useFormState(INITIAL);

  const { submitForm, isSubmitting, submitted, error } = useFormSubmit();

  const onSubmit = () => {
    const profile = safeLocalStorageGet("staff_profile");
    const { submittedBy, creatorName } = getSubmitterInfo(profile);
    const country = getDefaultCountry(profile);

    submitForm(formData, (attachments) => {
      const nocobasePayload = {
        user_name: creatorName,
        category: 1,
        Summary: formData.summary,
        description: formData.description,
        impactedsystem: formData.platforms ? [formData.platforms] : [],
        priority: formData.priority,
        environment: formData.environment,
        loanid: "",
        carid: "",
        dealerid: "",
        Description: `Category: ${formData.category}\n\n${formData.description}`,
        Country: country,
        sourceofrequest: "Staff Portal",
        confluencelink: "",
        accesstype: "",
        accesslevel: "",
        linemanagername: "",
        linemanageremail: "",
        mailinglist: "",
        status: "Open",
        tickettype: "Platform",
        Resolution: "",
        rootcause: "",
        f_7ygfivdcnao: "",
        Creator: creatorName,
        Creatoremail: submittedBy,
        attachement: attachments.map(a => a.id)
      };

      return { nocobasePayload };
    });
  };

  if (submitted) {
    return (
      <SplitFormLayout
        badge="Platform"
        title="Platform Request"
        subtitle="Request a new installation, application, or service."
        steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
        currentStep={STEPS.length}
        currentBreadcrumb="Request Submitted"
      >
        <SuccessCard
          title="Request Submitted"
          description="Your platform request has been submitted to the platform team."
          onBack={() => (window.location.href = "/staff/dashboard")}
          backLabel="Back to Dashboard"
        />
      </SplitFormLayout>
    );
  }

  const formValid =
    formData.summary &&
    formData.description &&
    formData.environment &&
    formData.category &&
    formData.priority &&
    (formData.category !== "Existing service" || formData.platforms);

  return (
    <SplitFormLayout
      badge="Platform"
      title="Platform Request"
      subtitle="Request a new installation, application, or service."
      steps={STEPS.map(step => ({ ...step, label: tText(step.label), desc: tText(step.desc) }))}
      currentBreadcrumb="Platform Request"
    >
      <FormProvider value={contextValue}>
      <form
        onSubmit={(e) => e.preventDefault()}
        className={styles.ticketFormWrapper}
      >
        {error && <div className={styles.errorBanner}>{tText(error)}</div>}

        <FormSection id="basic" title="1. Basic Info">
          <FormField label="Summary" name="summary" required style={{ marginBottom: "20px" }}>
            <input
              type="text"
              name="summary"
              value={formData.summary}
              onChange={handleChange}
              className={styles.input}
              placeholder={tText("Brief summary of the request")}
              required
            />
          </FormField>
          <FormField label="Description" name="description" required>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleChange}
              className={styles.textarea}
              rows={5}
              placeholder={tText("Describe the request in detail...")}
              required
            />
          </FormField>
        </FormSection>

        <FormSection id="details" title="2. Request Details">
          <div className={styles.row2} style={{ marginBottom: "20px" }}>
            <FormField label="Environment" name="environment" required>
              <select
                name="environment"
                value={formData.environment}
                onChange={handleChange}
                className={styles.select}
                required
              >
                {["Production", "Staging", "Development"].map((env) => (
                  <option key={env} value={env}><T>{env}</T></option>
                ))}
              </select>
            </FormField>
            <FormField label="Category" name="category" required>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className={styles.select}
                required
              >
                {[
                  "New installation",
                  "New Application",
                  "service",
                  "Emails",
                  "Existing service",
                ].map((cat) => (
                  <option key={cat} value={cat}><T>{cat}</T></option>
                ))}
              </select>
            </FormField>
          </div>
          <div className={styles.row2}>
            <FormField label="Priority" name="priority" required>
              <select
                name="priority"
                value={formData.priority}
                onChange={handleChange}
                className={styles.select}
                required
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}><T>{p}</T></option>
                ))}
              </select>
            </FormField>
            {formData.category === "Existing service" && (
              <FormField label="Platforms" name="platforms" required>
                <select
                  name="platforms"
                  value={formData.platforms}
                  onChange={handleChange}
                  className={styles.select}
                  required
                >
                  <option value="">{tText("Select...")}</option>
                  {IMPACTED_MODULES.map((m) => (
                    <option key={m} value={m}><T>{m}</T></option>
                  ))}
                </select>
              </FormField>
            )}
          </div>
        </FormSection>

        <FormSection id="attachments" title="3. Attachments">
          <FileAttachment
            attachments={formData.attachments}
            onChange={handleAddFiles}
            onRemove={handleRemoveFile}
          />
        </FormSection>

        <PinnedSubmit
          isSubmitting={isSubmitting}
          isValid={formValid}
          onSubmit={onSubmit}
          onInvalidSubmit={setAllTouched}
          note="Your request will be sent to the Platform team."
        />
      </form>
      </FormProvider>
    </SplitFormLayout>
  );
}
